// Puller do sync — TRAZ o delta do servidor pro local-db (merge LWW). Plan:
// docs/plan/sync-conta-cross-device.md (S4).
//
// QUANDO roda (oportunista, NÃO polling — premissa de infra):
//   - no boot (se há sessão Swipe);
//   - ao trocar de perfil;
//   - ao voltar do background (visibilitychange→visible).
//
// O QUE faz:
//   1. pullProfiles: baixa /profiles e ADOTA os perfis remotos no local-db com o
//      MESMO uuid do servidor (reconciliação — sem isso os itens, chaveados por
//      profileId, não casariam entre devices). Tombstones removem o local.
//   2. pullItems: pagina /sync?profileId=&cursor= e aplica cada item no local-db
//      via applyRemoteSyncItem (merge LWW por conteúdo), recompondo a key local.
//      Guarda o cursor por perfil (delta incremental — só o que mudou desde então).

import { applyRemoteProfile, applyRemoteSyncItem } from '../local-db'
import { track } from '../qoe'
import { accountKey, useSessionStore } from '../store'
import { resolveSurface } from '../surface'

import { SYNC_REQUEST_TIMEOUT_MS } from './net'
import { getSessionGeneration, getSwipeUserId } from './swipe-session'
import { listRemoteProfiles, pullSync, type PullTiming } from './sync-api'
import { composeLocalId } from './sync-scope'

// Cursor de pull por (swipeUser, perfil) em localStorage — delta incremental.
const CURSOR_PREFIX = 'iptv:sync-cursor:'

// ── A página numa rede lenta (AP-116, review de 29/09/2026) ──
// Todo pedido do sync tem prazo de 15s (sync/net.ts). A página do servidor tem até 500
// itens (~250 KB): abaixo de ~130 kbps, ou com o backend lento na 1ª página, ela
// estourava SEMPRE, e o cursor nunca andava. Agora, quando a página estoura o prazo, o
// pull repete a MESMA página (mesmo cursor) com o `limit` pela metade, até o piso. O
// tamanho reduzido vale pros pulls seguintes, e VOLTA A DOBRAR depois de
// PULL_PAGE_GROW_AFTER páginas CHEIAS seguidas que chegaram com folga, até a página
// cheia: na TV o app vive dias, e uma rede ruim pontual não pode deixar todo pull no piso
// (20× mais pedidos, e a trava de 100 páginas por pull limitando a 2.500 itens). Só a
// página cheia e rápida é prova de que o dobro cabe; a curta (um delta pequeno) não diz
// nada, e contá-la fazia a página oscilar numa rede fixa (review de 29/09/2026: onde cabe
// 62 e não 124, 40 pulls de delta curto estouravam 5 prazos, alternando pra sempre). O
// cursor só anda com uma página que chegou inteira: a que estourou o prazo não é lida (a
// corrida a descarta, mesmo que chegue depois).
/** A página do servidor (`SYNC_PAGE_SIZE` de apps/api). Sem `limit`, é ela. */
export const PULL_PAGE_MAX = 500
/**
 * Piso da página. Derivação: ~0,5 KB por item (500 itens ≈ 250 KB) → 25 itens ≈ 12 KB,
 * que cabem nos 15s do prazo numa rede de ~7 kbps. Abaixo disso a rede não sustenta nem
 * o app; e com página menor o pull de um perfil grande vira dezenas de pedidos.
 */
export const PULL_PAGE_MIN = 25
/**
 * Páginas CHEIAS seguidas que chegaram com folga (ver PULL_GROW_MAX_ELAPSED_FRACTION)
 * antes de tentar o dobro. Oito provas seguidas, e não uma: a vazão de uma TV varia, e
 * uma tentativa do dobro que estoura custa um prazo inteiro (15s). Do piso à página cheia
 * são 5 dobras, 40 páginas cheias: é a carga grande (o 1º pull completo) que recupera o
 * tamanho; os deltas pequenos de depois não mudam o tamanho, e também quase não
 * dependem dele.
 */
export const PULL_PAGE_GROW_AFTER = 8
/**
 * Fração do prazo (`SYNC_REQUEST_TIMEOUT_MS`) abaixo da qual uma página cheia prova que o
 * dobro cabe. Derivação: na mesma vazão, o tempo de transferência cresce com o tamanho;
 * uma página cheia em menos de 40% do prazo (6 de 15s) prevê o dobro em menos de 80%
 * (12s), com folga pra latência e variação. Uma página cheia que chegou devagar (acima
 * disso) é prova do contrário: zera a contagem.
 */
export const PULL_GROW_MAX_ELAPSED_FRACTION = 0.4
/** O tamanho de página em uso nesta execução do app. */
let pullPageLimit = PULL_PAGE_MAX
/** Páginas cheias seguidas que chegaram com folga desde a última redução (ou dobra). */
let pagesFitInARow = 0

function cursorKey(swipeUserId: string, profileId: string): string {
  return `${CURSOR_PREFIX}${swipeUserId}:${profileId}`
}

function getCursor(swipeUserId: string, profileId: string): string | null {
  if (typeof localStorage === 'undefined') return null
  try {
    return localStorage.getItem(cursorKey(swipeUserId, profileId))
  } catch {
    return null
  }
}

function setCursor(swipeUserId: string, profileId: string, cursor: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(cursorKey(swipeUserId, profileId), cursor)
  } catch {
    // ignora
  }
}

/** Limpa todos os cursores de pull (troca de conta/logout). */
export function clearPullCursors(): void {
  if (typeof localStorage === 'undefined') return
  try {
    const toRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k?.startsWith(CURSOR_PREFIX)) toRemove.push(k)
    }
    for (const k of toRemove) localStorage.removeItem(k)
  } catch {
    // ignora
  }
}

/** Contexto de sync do device atual (provider + username + perfil ativo). */
interface PullContext {
  providerCode: string
  account: string // `${providerCode}:${username}`
  profileId: string
}

/**
 * Conta sincronizável atual (provider com CÓDIGO + username), SEM exigir perfil
 * ativo. Usado pelo pull de PERFIS — que precisa rodar ANTES de o usuário
 * escolher um perfil (device novo baixa os perfis da conta pra poder escolher).
 */
function currentAccount(): { providerCode: string; account: string } | null {
  const s = useSessionStore.getState()
  if (!s.providerCode || !s.username) return null // só login com código (não direct)
  const account = accountKey(s)
  if (!account) return null
  return { providerCode: s.providerCode, account }
}

/** Contexto COM perfil ativo — usado pelo pull de ITENS (chaveados por perfil). */
function currentContext(): PullContext | null {
  const base = currentAccount()
  if (!base) return null
  const profileId = useSessionStore.getState().activeProfileId
  if (!profileId || profileId === '_main') return null
  return { providerCode: base.providerCode, account: base.account, profileId }
}

/**
 * Desfecho do pull de PERFIS (o que a tela de perfis precisa saber pra não oferecer
 * "criar perfil" a quem ainda não viu os perfis da conta):
 *  - `ok`: o servidor respondeu; `remoteIds` = os perfis VIVOS da conta.
 *  - `failed`: havia sessão e contexto, mas a lista não veio (rede, prazo, 401...).
 *  - `no-context`: há sessão Swipe sem contexto de sync (sem provider com código +
 *    usuário no store): lista direta, ou a fonte ainda não foi hidratada.
 *  - `no-session`: sem conta Swipe — o app é local, não há o que puxar.
 */
export type ProfilesPullOutcome =
  | { status: 'ok'; remoteIds: ReadonlySet<string> }
  | { status: 'failed' }
  | { status: 'no-context' }
  | { status: 'no-session' }

/** O que um `syncPull` fez (hoje, só o desfecho dos perfis interessa a quem chama). */
export interface SyncPullResult {
  profiles: ProfilesPullOutcome
  /**
   * DE QUEM é este resultado: a geração da sessão + a conta (`profilesSessionKey`)
   * lidas no COMEÇO do pull; `null` sem sessão ou sem contexto. Quem decide uma tela
   * pelo resultado de um pull compartilhado confere esta chave contra a de agora
   * (`ensureProfilesPulled`): o pull em voo pode ser de outra conta ou de outra sessão.
   */
  key: string | null
}

let pullInflight: Promise<SyncPullResult> | null = null

/**
 * O 1º pull de perfis que DEU CERTO nesta sessão, por conta. É o que separa "a tela de
 * perfis abre pela 1ª vez depois do login" (precisa esperar os perfis da conta antes de
 * oferecer criar um) de "troquei de perfil no meio da sessão" (a lista local já é a da
 * conta, mostra na hora e atualiza por trás). Chave = geração da sessão + conta: um
 * login novo (outra geração) zera sozinho.
 */
let lastProfilesOk: { key: string; remoteIds: ReadonlySet<string> } | null = null

function profilesSessionKey(account: string): string {
  return `${getSessionGeneration()}|${account}`
}

/**
 * Os perfis VIVOS da conta, se o pull de perfis já deu certo nesta sessão pra esta
 * conta; senão `null` (a tela tem que esperar o pull). Ver `lastProfilesOk`.
 */
export function profilesPulledThisSession(account: string): ReadonlySet<string> | null {
  if (!lastProfilesOk || lastProfilesOk.key !== profilesSessionKey(account)) return null
  return lastProfilesOk.remoteIds
}

/**
 * Baixa os perfis da conta e adota no local-db (reconciliação de uuid). Usa o
 * `account` do device atual (providerCode:username). Best-effort. Devolve `null` se a
 * lista não veio; senão se ALGUM perfil mudou (pra o caller decidir refresh) e os ids
 * dos perfis vivos da conta.
 */
async function pullProfiles(account: string): Promise<{ changed: boolean; remoteIds: Set<string> } | null> {
  const profiles = await listRemoteProfiles()
  if (!profiles) return null
  let changed = false
  const remoteIds = new Set<string>()
  for (const p of profiles) {
    if (p.deletedAt == null) remoteIds.add(p.id)
    const didChange = await applyRemoteProfile({
      id: p.id,
      account,
      name: p.name,
      avatar: p.avatar ?? '👤',
      kind: p.kind,
      hasPin: p.hasPin,
      createdAt: Math.floor(new Date(p.createdAt).getTime() / 1000),
      deleted: p.deletedAt != null,
    })
    if (didChange) changed = true
  }
  // `changed` só é true se ALGUM perfil mudou de fato (evita refresh inútil).
  return { changed, remoteIds }
}

/**
 * Pull delta de itens de UM perfil. O cursor é SEMPRE opaco (composto ts+id) —
 * tanto no meio da paginação quanto como ponto de retomada do próximo delta. Isso
 * elimina a borda perigosa de um `since` só-por-timestamp (que perderia itens com
 * updated_at idêntico no corte da página). Pagina enquanto `hasMore`; guarda o
 * cursor final pra o próximo pull continuar exatamente dali (delta incremental).
 * Aplica cada item no local-db (merge LWW), recompondo a key local com o
 * providerCode/profileId DESTE device.
 */
async function pullItems(ctx: PullContext, swipeUserId: string): Promise<number> {
  const scope = `p:${ctx.providerCode}::pf:${ctx.profileId}`
  let cursor = getCursor(swipeUserId, ctx.profileId)
  let applied = 0
  // Loop de paginação: segue enquanto houver próxima página imediata.
  for (let guard = 0; guard < 100; guard++) {
    const requested = pullPageLimit
    // A duração SÓ do pedido da página (sem a espera pelo token): é ela que diz se o dobro
    // cabe na rede (review de 29/09/2026: um refresh de 7s contava como página lenta).
    const timing: PullTiming = { requestMs: null }
    const page = await pullSync(ctx.profileId, {
      ...(cursor ? { cursor } : {}),
      ...(requested < PULL_PAGE_MAX ? { limit: requested } : {}),
      timing,
    })
    if (page === 'timeout') {
      // A página não chegou inteira no prazo: o cursor NÃO anda. Repete a mesma página
      // com a metade do tamanho; no piso, desiste deste pull (o próximo começa do piso).
      pagesFitInARow = 0
      if (pullPageLimit <= PULL_PAGE_MIN) return applied
      pullPageLimit = Math.max(PULL_PAGE_MIN, Math.floor(pullPageLimit / 2))
      continue
    }
    if (!page) return applied // falha de rede/sessão → tenta no próximo pull
    // Só a página CHEIA é prova sobre o dobro (a curta, fim de um delta, é neutra): cheia e
    // com folga conta; cheia e devagar zera. Depois de PULL_PAGE_GROW_AFTER seguidas, o dobro.
    if (requested < PULL_PAGE_MAX && page.items.length >= requested) {
      if (timing.requestMs !== null && timing.requestMs < PULL_GROW_MAX_ELAPSED_FRACTION * SYNC_REQUEST_TIMEOUT_MS) {
        if (++pagesFitInARow >= PULL_PAGE_GROW_AFTER) {
          pullPageLimit = Math.min(PULL_PAGE_MAX, requested * 2)
          pagesFitInARow = 0
        }
      } else {
        pagesFitInARow = 0
      }
    }
    for (const item of page.items) {
      const localId = composeLocalId(ctx.providerCode, ctx.profileId, item.itemKey)
      const res = await applyRemoteSyncItem({
        localId,
        scope,
        kind: item.kind,
        payload: item.payload,
        deleted: item.deleted,
      })
      if (res === 'applied') applied += 1
    }
    // Guarda o cursor de retomada (vem sempre que há itens). Persistir a cada
    // página deixa o delta incremental resiliente a fechar o app no meio.
    if (page.cursor) {
      cursor = page.cursor
      setCursor(swipeUserId, ctx.profileId, cursor)
    }
    if (!page.hasMore) return applied // fim do delta; o cursor guardado retoma depois
  }
  return applied
}

// Handler de "sync aplicou dados novos" — registrado pelo React (providers.tsx)
// pra INVALIDAR as queries da Home/"continuar assistindo" quando o pull traz algo
// de outro device. Sync silencioso (decisão de produto): só atualiza a tela, sem
// badge/spinner. Fora de React (mesmo padrão do setOnAuthInvalid do app).
let onSyncApplied: (() => void) | null = null

/** Registra o handler de pós-pull. Retorna unsubscriber (hot-reload/testes). */
export function setSyncAppliedHandler(handler: () => void): () => void {
  onSyncApplied = handler
  return () => {
    if (onSyncApplied === handler) onSyncApplied = null
  }
}

/**
 * Orquestra o pull: perfis + itens do perfil ativo. Idempotente (1 inflight).
 * Best-effort — qualquer falha só adia pro próximo gatilho. No-op sem sessão
 * Swipe ou sem contexto sincronizável.
 */
export async function syncPull(): Promise<SyncPullResult> {
  if (pullInflight) return pullInflight
  // ── O inflight PRESO (10/10/2026) ──
  // Isto era `pullInflight = (async () => { try {…} finally { pullInflight = null } })()`.
  // Quando o corpo termina SEM nenhum `await` (sem sessão Swipe, ou sessão sem contexto:
  // os dois `return` cedo), a função async roda INTEIRA, `finally` incluído, ANTES de a
  // atribuição acontecer: o `= null` vinha primeiro e a promessa já resolvida era gravada
  // DEPOIS, pra sempre. Todo `syncPull` seguinte devolvia aquele resultado velho sem
  // puxar nada, até recarregar a página. Gatilho real: o pull do `visibilitychange` (ligado
  // no boot, com ou sem sessão) disparar antes do pareamento — trocar de aba no PC, a TV
  // voltar do standby — e daí a tela de perfis e a entrada no perfil não puxavam perfis
  // nem itens da conta. A limpeza agora só solta o inflight que ELA guardou, depois de
  // gravado.
  const run = runSyncPull()
  pullInflight = run
  const release = (): void => {
    if (pullInflight === run) pullInflight = null
  }
  void run.then(release, release)
  return run
}

async function runSyncPull(): Promise<SyncPullResult> {
  const swipeUserId = getSwipeUserId()
  if (!swipeUserId) return { profiles: { status: 'no-session' }, key: null }
  // 1) PERFIS — roda mesmo SEM perfil ativo (device novo baixa os perfis da
  //    conta pra poder escolher; é o caso de uso principal do cross-device).
  const acct = currentAccount()
  if (!acct) {
    // Sessão Swipe sem contexto: nada sobe nem desce neste aparelho. Era o estado
    // PERMANENTE da superfície mobile (10/10/2026), calado. Deixa rastro.
    reportSyncContextMissing('no_account')
    return { profiles: { status: 'no-context' }, key: null }
  }
  // A chave da sessão é lida ANTES do pedido (cybersec P2): lida depois do `await`, um
  // pareamento de outra conta no meio carimbava a lista da conta velha como "já puxei"
  // da sessão nova. Se a sessão mudou durante o pedido, o resultado não vale pra ninguém.
  const key = profilesSessionKey(acct.account)
  const pulled = await pullProfiles(acct.account)
  const sameSession = profilesSessionKey(acct.account) === key
  const profiles: ProfilesPullOutcome = pulled && sameSession
    ? { status: 'ok', remoteIds: pulled.remoteIds }
    : { status: 'failed' }
  if (pulled && sameSession) lastProfilesOk = { key, remoteIds: pulled.remoteIds }
  // 2) ITENS — só com perfil ativo (chaveados por perfil).
  const ctx = currentContext()
  let itemsApplied = 0
  if (ctx) itemsApplied = await pullItems(ctx, swipeUserId)
  // Notifica o React pra atualizar a Home/"continuar assistindo" SE algo mudou
  // (sync silencioso: só refresh, sem UI). Evita invalidação inútil quando o
  // delta veio vazio (caso comum dos pulls oportunistas).
  if ((pulled?.changed || itemsApplied > 0) && onSyncApplied) onSyncApplied()
  return { profiles, key }
}

/**
 * Garante um pull de perfis CONCLUSIVO pra quem precisa decidir a tela (perfis da TV e
 * do mobile). O `syncPull` compartilha o pull em voo: se o que estava em voo começou
 * ANTES de o contexto existir (o pull do boot, disparado antes de a fonte ser
 * hidratada), ele termina em `no-context` mesmo com o contexto já presente agora. Aí
 * roda um pull novo, uma vez.
 */
export async function ensureProfilesPulled(): Promise<ProfilesPullOutcome> {
  const first = await syncPull()
  // O resultado só vale se for DESTA sessão e DESTA conta (code-review, rodada 5). O pull
  // em voo pode ser de antes da troca de fonte (`ok` com os perfis gravados sob a conta
  // velha: a conta nova abria vazia e a TV oferecia "criar" de novo), de antes de um
  // re-pareamento (`failed` sem motivo) ou de antes de o contexto existir (`no-context`).
  const acct = currentAccount()
  // Sem contexto AGORA, o resultado em voo (que pode ser um `ok` de outra conta) não vale
  // pra ninguém: `no-context` (rodada 6, cybersec P3). Sem sessão segue `no-session`.
  if (acct === null) return first.profiles.status === 'no-session' ? first.profiles : { status: 'no-context' }
  if (first.key === profilesSessionKey(acct.account)) return first.profiles
  return (await syncPull()).profiles
}

// ── Telemetria `sync_context_missing` (uma vez por sessão e motivo) ──
const contextMissingReported = new Set<string>()

/**
 * Rastro de "sessão Swipe sem contexto de sync" (ver o evento em qoe.ts). Uma vez por
 * geração da sessão e motivo: o puller roda no boot, na volta do background e em cada
 * troca de perfil, e o estado não muda entre eles.
 */
export function reportSyncContextMissing(reason: 'no_account' | 'no_source' | 'resolve_failed'): void {
  const key = `${getSessionGeneration()}|${reason}`
  if (contextMissingReported.has(key)) return
  contextMissingReported.add(key)
  track({
    type: 'sync_context_missing',
    ts: Math.floor(Date.now() / 1000),
    reason,
    surface: resolveSurface(),
  })
}

let listenersBound = false
let lastVisiblePullAt = 0
// Throttle do pull por visibilitychange: alt-tab rápido (sair/voltar em segundos)
// não dispara N pulls. 30s cobre o caso real (voltou pra TV depois de mexer no
// celular) sem refetch a cada foco de janela. O boot e a troca de perfil não
// passam por aqui (chamam syncPull direto).
const VISIBLE_PULL_THROTTLE_MS = 30_000

/** Liga o pull oportunista ao voltar do background. Idempotente. */
export function bindPullEvents(): void {
  if (listenersBound) return
  if (typeof document === 'undefined') return
  listenersBound = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    const now = Date.now()
    if (now - lastVisiblePullAt < VISIBLE_PULL_THROTTLE_MS) return
    lastVisiblePullAt = now
    void syncPull()
  })
}
